import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import Button from '../Button/index.tsx';
import { PHONE_MEDIA_QUERY } from '../../common/responsive.js';
import GiftCard from '../GiftCard/index.jsx';
import TokenInformation from '../TokenInformation/index.jsx';
import Information from '../Information/index.jsx';
import TokenSwapInputs from '../TokenSwapInputs';
import TextInput from '../Inputs/Text/index.jsx';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { fetchImageAsBase64 } from '../../utils/images';
import { useSolana } from '../../hooks/useSolana.js';
import { useFlag } from '@unleash/proxy-client-react';
import { UNLEASH_ENABLED_BUY_VETS } from '../../constants/index.jsx';
import { useSelector } from 'react-redux';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
} from '../../redux/slices/tokenSlice';
import { formatTokenPrice } from '../../common/currency.js';
import ScrollRestore from '../ScrollRestore/index.tsx';

const GiftCardContainer = styled.div`
  width: 417px;
  height: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const schema = yup
  .object({
    email: yup
      .string()
      .email('Must be a valid email')
      .required('Email is required'),
    message: yup
      .string()
      .optional()
      .max(190, 'Message must be at most 190 characters'),
    cardImage: yup.string().required('Card image is required'),
    token: yup.string().required('Token address is required'),
    network: yup.string().required('Network is required'),
    baseToken: yup.string().required('Base token address is required'),
    baseTokenAmount: yup
      .string()
      .required('Amount is required')
      .test(
        'is-number',
        'Must be a valid number',
        (value) => !isNaN(value) && parseFloat(value) > 0,
      )
      .test('min-amount', 'Minimum amount is required', function (value) {
        const { minTokenAmount } = this.options.context;
        if (parseFloat(value) < minTokenAmount) {
          return this.createError({
            message: `Minimum amount is ${formatTokenPrice(minTokenAmount)}`,
          });
        }
        return true;
      }),
  })
  .required();

const DemoContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-end;
  flex: 1;
`;

export const Line = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
`;

const Message = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.status.error.primary};
  text-align: center;
`;

const SummaryTitle = styled.p`
  font-size: 22px;
  font-weight: 600;
`;

const SummaryDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ContentRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  width: 100%;

  @media ${PHONE_MEDIA_QUERY} {
    grid-template-columns: 1fr;
  }
`;

const GiftCardForm = ({
  initialValues = {
    email: '',
    message: '',
    cardImage: '',
    token: '',
    network: '',
    baseToken: '',
    baseTokenAmount: '',
  },
  onSubmit,
  submitButtonText = 'Send Now',
  showBackButton = true,
  backButtonText = 'Token Details',
  backButtonPath,
  secondaryButtonText,
  onSecondaryButtonClick,
  checkoutFlow = false,
  onFormValuesChange,
}) => {
  const navigate = useNavigate();
  const [submissionError, setSubmissionError] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const { fromTokenAmount, getTokenPrice } = useSolana();
  const vetsEnabled = useFlag(UNLEASH_ENABLED_BUY_VETS);
  const [minTokenAmount, setMinTokenAmount] = useState(0);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    reValidateMode: 'onBlur',
    defaultValues: initialValues,
    context: { minTokenAmount },
  });
  const baseTokenAmount = watch('baseTokenAmount');
  const cardImage = watch('cardImage');
  const tokenAddress = watch('token');
  const baseTokenAddress = watch('baseToken');
  const network = watch('network');
  // Get token details using addresses

  const toToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, tokenAddress),
  );

  const baseToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, baseTokenAddress),
  );

  // Get USDC token
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, network, 'USDC'),
  );

  // Memoize token options to prevent unnecessary re-renders
  const baseTokenOptions = useMemo(() => {
    const options = [];
    if (usdcToken) {
      options.push(usdcToken);
    }
    if (
      toToken &&
      toToken.address &&
      (!usdcToken || toToken.address !== usdcToken.address)
    ) {
      options.push(toToken);
    }
    return options;
  }, [usdcToken, toToken]);

  const toTokenOptions = useMemo(() => {
    return toToken ? [toToken] : [];
  }, [toToken]);

  useEffect(() => {
    // Set default USDC as base token if not set
    if (!baseTokenAddress && network && usdcToken) {
      setValue('baseToken', usdcToken.address);
    }
  }, [network, baseTokenAddress, setValue, usdcToken]);

  // Calculate all amounts based on the same logic as Confirmation
  let tokenAmount = 0;
  let amountUSD = 0;
  let feeUSD = 0;
  let totalAmountUSD = 0;
  let transferFeeUSD = 0;

  const isSameToken = baseToken?.address === toToken?.address;

  if (isSameToken) {
    // When using the same token (e.g., VETS to VETS)
    totalAmountUSD = fromTokenAmount(
      quoteData?.quote?.outAmount,
      usdcToken?.decimals || 6,
    );
    tokenAmount = fromTokenAmount(
      quoteData?.amounts?.swapOutputAmount,
      baseToken?.decimals || 6,
    );
    feeUSD = fromTokenAmount(
      quoteData?.feesUSD?.totalFee,
      usdcToken?.decimals || 6,
    );
    amountUSD = fromTokenAmount(
      quoteData?.amountsUSD?.swapOutputAmount,
      usdcToken?.decimals || 6,
    );
    transferFeeUSD = fromTokenAmount(
      quoteData?.feesUSD?.transferFee,
      usdcToken?.decimals || 6,
    );
  } else {
    tokenAmount = quoteData?.quote?.outAmount
      ? fromTokenAmount(quoteData.quote.outAmount, toToken?.decimals || 9)
      : 0;
    // Use swap quote to consider transfer fees
    amountUSD = fromTokenAmount(
      quoteData?.amountsUSD?.swapOutputAmount,
      usdcToken?.decimals || 6,
    );
    feeUSD = fromTokenAmount(
      quoteData?.fees?.totalFee,
      baseToken?.decimals || 6,
    );
    totalAmountUSD = quoteData?.amounts?.totalInputAmount
      ? fromTokenAmount(
          quoteData.amounts.totalInputAmount,
          baseToken?.decimals || 9,
        )
      : 0;
    transferFeeUSD = fromTokenAmount(
      quoteData?.feesUSD?.transferFee,
      usdcToken?.decimals || 6,
    );
  }

  const formattedTokenAmount = tokenAmount.toFixed(2);
  const formattedAmountUSD = amountUSD.toFixed(2);
  const formattedFee = feeUSD.toFixed(2);
  const formattedTransferFee = transferFeeUSD.toFixed(2);
  const formattedTotalAmount = totalAmountUSD.toFixed(2);

  const isVets = toToken?.symbol === 'VETS';

  useEffect(() => {
    const getMinTokenAmount = async () => {
      if (baseToken.symbol === 'USDC') {
        setMinTokenAmount(1);
        return;
      }

      const tokenPrice = await getTokenPrice(baseToken);
      if (tokenPrice) {
        // Calculate minimum token amount needed to reach $1
        const minAmount = 1 / tokenPrice;
        setMinTokenAmount(minAmount);
      }
    };
    getMinTokenAmount();
  }, [toToken, getTokenPrice, baseToken]);

  useEffect(() => {
    const setDefaultImage = async () => {
      const defaultImage = await fetchImageAsBase64(
        toToken?.giftCardImages[0]?.imageUrl,
      );
      setValue('cardImage', defaultImage);
    };
    if (!cardImage && toToken?.symbol) {
      setDefaultImage();
    }
  }, [cardImage, toToken, setValue]);

  const handleFromAmountChange = (value) => {
    setValue('baseTokenAmount', value, { shouldValidate: true });
  };

  const handleCardImageChange = (value) => {
    setValue('cardImage', value);
  };

  const handleQuoteChange = (quote) => {
    setQuoteData(quote);
  };

  const handleFromTokenChange = (token) => {
    setValue('baseToken', token.value, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('baseTokenAmount', '', {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleToTokenChange = (token) => {
    setValue('token', token.value, { shouldValidate: true, shouldDirty: true });
    setValue('network', token.network, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleFormSubmit = async (values) => {
    try {
      if (isVets && !vetsEnabled) {
        setSubmissionError('VETS is not yet available for purchase');
        return;
      }
      setSubmissionError(null);
      await onSubmit(values);
    } catch (error) {
      const errorMessage =
        error?.data?.error || error?.message || 'An unknown error occurred';
      setSubmissionError(errorMessage);
    }
  };

  // Add this useEffect to watch form values and notify parent
  useEffect(() => {
    const subscription = watch((value) => {
      onFormValuesChange?.(value);
    });
    return () => subscription.unsubscribe();
  }, [watch, onFormValuesChange]);

  return (
    <DemoContainer>
      {showBackButton && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          <img src='/chevron-left.svg' alt='Back' height={48} width={9} />
          <Button variant='link' onClick={() => navigate(backButtonPath)}>
            {backButtonText}
          </Button>
        </div>
      )}
      <ContentRow>
        <ScrollRestore />
        <GiftCardContainer>
          <GiftCard
            editable={true}
            tokenAmount={tokenAmount}
            cardImage={cardImage}
            token={toToken}
            onCardImageChange={handleCardImageChange}
          />
        </GiftCardContainer>
        {toToken && <TokenInformation token={toToken} />}
      </ContentRow>
      <Line />
      <ContentRow>
        <Information />
        <FormContainer>
          <div style={{ width: '100%' }}>
            <TokenSwapInputs
              fromAmount={baseTokenAmount}
              onFromAmountChange={handleFromAmountChange}
              onFromTokenChange={handleFromTokenChange}
              onToTokenChange={handleToTokenChange}
              error={errors?.baseTokenAmount?.message}
              slippageBps={import.meta.env.VITE_SLIPPAGE_BPS || 'auto'}
              showToBalance={false}
              showFromBalance={!checkoutFlow}
              showMaxButton={!checkoutFlow}
              fromTokens={baseTokenOptions}
              toTokens={toTokenOptions}
              isGift={true}
              onQuoteChange={handleQuoteChange}
              baseTokenAddress={baseTokenAddress}
            />
          </div>
          <Controller
            name='email'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Recipient Email'
                type='email'
                placeholder='recipient@example.com'
                error={errors?.email?.message}
                {...field}
              />
            )}
          />
          <Controller
            name='message'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Personal Message'
                type='text'
                placeholder='Write a personal message'
                error={errors?.message?.message}
                {...field}
              />
            )}
          />
          <SummaryRow>
            <SummaryTitle>
              {formattedTokenAmount} {toToken?.symbol || ''} Gift Card
            </SummaryTitle>
            <SummaryTitle>${formattedAmountUSD}</SummaryTitle>
          </SummaryRow>
          {feeUSD > 0 && (
            <SummaryRow>
              <SummaryDescription>Gifting Fee</SummaryDescription>
              <SummaryDescription>+ ${formattedFee}</SummaryDescription>
            </SummaryRow>
          )}
          {transferFeeUSD > 0 && (
            <SummaryRow>
              <SummaryDescription>Cause Fee</SummaryDescription>
              <SummaryDescription>+ ${formattedTransferFee}</SummaryDescription>
            </SummaryRow>
          )}
          <SummaryRow>
            <SummaryTitle>Total:</SummaryTitle>
            <SummaryTitle>${formattedTotalAmount}</SummaryTitle>
          </SummaryRow>
          <div style={{ display: 'flex', gap: '20px' }}>
            {secondaryButtonText && (
              <Button
                variant='secondary'
                onClick={onSecondaryButtonClick}
                disabled={isSubmitting}
              >
                {secondaryButtonText}
              </Button>
            )}
            <Button
              onClick={handleSubmit(handleFormSubmit)}
              loading={isSubmitting}
              disabled={!isValid || isSubmitting || (isVets && !vetsEnabled)}
            >
              {submitButtonText}
            </Button>
          </div>
          <Message>
            {isVets && !vetsEnabled && (
              <div style={{ textAlign: 'center' }}>
                VETS is not yet available for purchase.
              </div>
            )}
            {submissionError && submissionError}
          </Message>
        </FormContainer>
      </ContentRow>
    </DemoContainer>
  );
};

export default GiftCardForm;
